import {Alert, Paper, Stack, Typography} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {type BillingGranularity, BillingInfo, type LegalEntity} from "../AdminTypes";
import {type BillingMetricKey, formatDateLabel} from "./billingUtils";
import {TFunction} from "i18next";
import {useState} from "react";
import {useDownloadBillingCsv} from "../../../api/admin/adminQueries";
import {BarChartButtonGroup} from "./UsageBarChart/BarChartButtonGroup";
import {BarChartTitle} from "./UsageBarChart/BarChartTitle";
import UsageBarChart from "./UsageBarChart/UsageBarChart";

const noEntitySelectedView = (translate: TFunction) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            p: {xs: 3, md: 4},
            borderRadius: 3,
            border: "1px dashed",
            borderColor: alpha(theme.palette.primary.main, 0.28),
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.88 : 0.94),
        })}
    >
        <Stack spacing={1.5} alignItems="center" textAlign="center">
            <CalendarMonthRoundedIcon sx={{fontSize: 40, color: "primary.main"}} />
            <Typography sx={{fontWeight: 800, color: "text.primary"}}>
                {translate("admin.billing.emptyState.title")}
            </Typography>
            <Typography color="text.secondary" sx={{maxWidth: 520}}>
                {translate("admin.billing.emptyState.description")}
            </Typography>
        </Stack>
    </Paper>
);

const summaryBanner = (
    translate: TFunction<"translation", undefined>,
    selectedStartDate: string,
    selectedEndDate: string,
    selectedGranularity: BillingGranularity,
    selectedLegalEntity: LegalEntity | null,
) => (
    <Alert
        severity="info"
        sx={(theme) => ({
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.08 : 0.16),
            color: theme.palette.text.primary,
            ".MuiAlert-icon": {color: theme.palette.primary.main},
            alignItems: "center",
        })}
    >
        {translate("admin.billing.selectionSummary", {
            startDate: formatDateLabel(selectedStartDate, {
                month: "short",
                day: "numeric",
                year: "numeric",
            }),
            endDate: formatDateLabel(selectedEndDate, {
                month: "short",
                day: "numeric",
                year: "numeric",
            }),
            granularity: translate(`admin.billing.granularity.${selectedGranularity.toLowerCase()}`),
            legalEntityName: selectedLegalEntity?.name ?? translate("admin.billing.selectedLegalEntityFallback"),
        })}
    </Alert>
);

type BillingOverviewProps = {
    billingInfo: BillingInfo | undefined;
    errorMessage: string;
    isBillingLoading: boolean;
    selectedEndDate: string;
    selectedGranularity: BillingGranularity;
    selectedLegalEntity: LegalEntity | null;
    selectedStartDate: string;
};

const BillingOverview = ({
    billingInfo,
    errorMessage,
    isBillingLoading,
    selectedEndDate,
    selectedGranularity,
    selectedLegalEntity,
    selectedStartDate,
}: BillingOverviewProps) => {
    const {t: translate} = useTranslation();
    const [selectedMetric, setSelectedMetric] = useState<BillingMetricKey>("tokens");
    const billingItems = billingInfo?.items ?? [];
    const maxMetricValue = Math.max(
        1,
        ...billingItems.map((item) => (selectedMetric === "price" ? item.price : item.totalTokenAmount)),
    );

    const hasSelectedLegalEntity: boolean = selectedLegalEntity !== null;

    const cycleEnd = billingInfo?.cycleEnd || "";
    const cycleStart = billingInfo?.cycleStart || "";

    const activeGranularity = billingInfo?.granularity ?? selectedGranularity;
    const chartWidth = Math.max(billingItems.length * 108, 720);

    const hasCompleteDateRange = selectedStartDate !== "" && selectedEndDate !== "";
    const hasValidDateRange = hasCompleteDateRange && selectedStartDate <= selectedEndDate;

    const canDownloadBillingCsv = hasSelectedLegalEntity && hasValidDateRange;
    const downloadBillingCsvMutation = useDownloadBillingCsv();

    const downloadErrorMessage = downloadBillingCsvMutation.error?.message || "";

    const showMissingRangeAlert = hasSelectedLegalEntity && !hasCompleteDateRange;
    const showInvalidRangeAlert = hasSelectedLegalEntity && hasCompleteDateRange && !hasValidDateRange;

    const handleDownloadBillingCsv = async () => {
        if (!selectedLegalEntity || !hasValidDateRange) {
            return;
        }

        await downloadBillingCsvMutation.mutateAsync({
            legalEntityId: selectedLegalEntity.id,
            startDate: selectedStartDate,
            endDate: selectedEndDate,
            timeGranularity: selectedGranularity,
        });
    };

    return (
        <>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {downloadErrorMessage && <Alert severity="error">{downloadErrorMessage}</Alert>}
            {showMissingRangeAlert && <Alert severity="warning">{translate("admin.billing.rangeIncomplete")}</Alert>}
            {showInvalidRangeAlert && <Alert severity="warning">{translate("admin.billing.invalidRange")}</Alert>}

            {!hasSelectedLegalEntity ? (
                noEntitySelectedView(translate)
            ) : (
                <>
                    {summaryBanner(
                        translate,
                        selectedStartDate,
                        selectedEndDate,
                        selectedGranularity,
                        selectedLegalEntity,
                    )}

                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            p: {xs: 2.5, md: 3},
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                            bgcolor: alpha(
                                theme.palette.background.paper,
                                theme.palette.mode === "light" ? 0.88 : 0.94,
                            ),
                        })}
                    >
                        <Stack spacing={3}>
                            <Stack
                                direction={{xs: "column", md: "row"}}
                                justifyContent="space-between"
                                alignItems={{xs: "flex-start", md: "center"}}
                                spacing={2}
                            >
                                <BarChartTitle
                                    cycleStart={cycleStart}
                                    selectedStartDate={selectedStartDate}
                                    cycleEnd={cycleEnd}
                                    selectedEndDate={selectedEndDate}
                                />

                                <BarChartButtonGroup
                                    onDownloadBillingCsv={handleDownloadBillingCsv}
                                    canDownloadBillingCsv={canDownloadBillingCsv}
                                    isDownloadPending={downloadBillingCsvMutation.isPending}
                                    selectedMetric={selectedMetric}
                                    onSelectedMetricChange={setSelectedMetric}
                                    isBillingLoading={isBillingLoading}
                                    hasValidDateRange={hasValidDateRange}
                                />
                            </Stack>

                            {isBillingLoading ? (
                                <Typography color="text.secondary">{translate("admin.billing.loading")}</Typography>
                            ) : (
                                <UsageBarChart
                                    billingItems={billingItems}
                                    chartWidth={chartWidth}
                                    selectedMetric={selectedMetric}
                                    activeGranularity={activeGranularity}
                                    maxMetricValue={maxMetricValue}
                                />
                            )}
                        </Stack>
                    </Paper>
                </>
            )}
        </>
    );
};

export default BillingOverview;
